"use client";

import type { Tour } from "@/types";
import { useEffect, useRef, useState } from "react";
import TourGridCard from "../cards/TourGridCard";

type ToursSliderProps = {
  tours: Tour[];
};

export default function ToursSlider({ tours }: ToursSliderProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const cardWidthRef = useRef<number | null>(null);
  const [scrollIndex, setScrollIndex] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    if (!scrollRef.current) return;
    const firstChild = scrollRef.current.children[0] as HTMLElement | undefined;
    if (!firstChild) return;
    cardWidthRef.current = firstChild.offsetWidth + 24;
  }, []);

  const scroll = (direction: -1 | 1) => {
    if (!mounted || !scrollRef.current) return;
    const cardWidth = cardWidthRef.current;
    if (!cardWidth) return;
    scrollRef.current.scrollBy({
      left: direction * cardWidth,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (!mounted) return;
    const handleScroll = () => {
      if (!scrollRef.current) return;
      const cardWidth = cardWidthRef.current;
      if (!cardWidth) return;
      const index = Math.round(scrollRef.current.scrollLeft / cardWidth);
      setScrollIndex(index);
    };

    const el = scrollRef.current;
    if (el) el.addEventListener("scroll", handleScroll);
    return () => {
      if (el) el.removeEventListener("scroll", handleScroll);
    };
  }, [mounted]);

  return (
    <div className='relative max-w-300 text-left py-10 w-full'>
      <button
        aria-label='Scroll tours left'
        onClick={() => scroll(-1)}
        className='absolute -left-12.5 top-1/2 transform -translate-y-1/2 hidden lg:flex w-10 h-10 bg-[#A3A3A3] cursor-pointer hover:bg-black bg-opacity-50 text-white rounded-full items-center justify-center transition hover:bg-opacity-80 z-10'
      >
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='24'
          height='24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <polyline points='15 18 9 12 15 6' />
        </svg>
      </button>

      <button
        aria-label='Scroll tours right'
        onClick={() => scroll(1)}
        className='absolute -right-12.5 top-1/2 transform -translate-y-1/2 hidden lg:flex w-10 h-10 bg-[#A3A3A3] cursor-pointer hover:bg-black bg-opacity-50 text-white rounded-full items-center justify-center transition hover:bg-opacity-80 z-10'
      >
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='24'
          height='24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <polyline points='9 18 15 12 9 6' />
        </svg>
      </button>

      <div className='relative overflow-hidden'>
        <div
          ref={scrollRef}
          className='flex gap-6 overflow-x-auto scroll-smooth no-scrollbar px-4 md:px-4'
          style={{
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0px, black clamp(25px,5vw,50px), black calc(100% - clamp(38px,7vw,75px)), transparent 100%)",
            maskImage:
              "linear-gradient(to right, transparent 0px, black clamp(25px,5vw,50px), black calc(100% - clamp(38px,7vw,75px)), transparent 100%)",
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskSize: "100% 100%",
            maskSize: "100% 100%",
            scrollbarWidth: "none", // Firefox
            msOverflowStyle: "none", // IE / old Edge
          }}
        >
          {tours
            .filter((tour): tour is Tour => Boolean(tour?.url))
            .map((tour, index) => (
              <div key={tour.url} className='flex-none w-65 md:w-75'>
                <TourGridCard tour={tour} />
              </div>
            ))}
        </div>
      </div>

      <div className='flex px-6 flex-wrap gap-y-1 md:justify-center mt-4 overflow-hidden'>
        {tours.map((_: Tour, index: number) => (
          <div
            key={index}
            className={`flex-none w-2.5 h-2.5 min-w-2.5 mx-1 rounded-full transition
              ${
                scrollIndex === index
                  ? "bg-gray-600 w-3 h-3"
                  : "bg-gray-400 opacity-50"
              }
              ${index >= tours.length - 2 ? "lg:hidden" : ""}
            `}
          />
        ))}
      </div>
    </div>
  );
}
